import { useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useChannels } from 'providers/useChannels/useChannels';
import { queryClient } from 'providers/QueryProvider';
import GSApi from '../../api/Api';
import { NowAndNextData } from '../../types/Asset';
import { parseNowAndNextResponse } from '../../utils/fnParser';
import { ProgramScheduleManager } from '../useProgramScheduleManager/useProgramScheduleManager';

const NOW_AND_NEXT_KEY = ['nowAndNext'] as const;
const DEFAULT_REFETCH_INTERVAL = 900_000;
const MIN_REFETCH_INTERVAL = 60_000;

export const useNowAndNextQuery = (enabled = true) => {
    const { channels } = useChannels();

    const { data, isLoading, error, refetch } = useQuery<NowAndNextData>({
        queryKey: NOW_AND_NEXT_KEY,
        queryFn: async () => {
            const { response, error: requestError, status } = await GSApi.getNowAndNext();

            if (!response?.results) {
                throw requestError instanceof Error
                    ? requestError
                    : new Error(`Now and next request failed with status ${status ?? 'unknown'}`);
            }

            return parseNowAndNextResponse(response, channels);
        },
        enabled: enabled && !!channels?.length,
        refetchInterval: ({ state }) => {
            if (!state.data) return DEFAULT_REFETCH_INTERVAL;
            const interval = Math.max(state.data.nextUpdateIn * 1000 || DEFAULT_REFETCH_INTERVAL, MIN_REFETCH_INTERVAL);
            return Math.max(state.data.lastUpdated + interval - Date.now(), MIN_REFETCH_INTERVAL);
        },
        staleTime: MIN_REFETCH_INTERVAL,
        gcTime: 30 * 60_000,
        refetchOnWindowFocus: false,
    });

    const scheduleManagerRef = useRef<ProgramScheduleManager | null>(null);
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    useEffect(() => {
        scheduleManagerRef.current = new ProgramScheduleManager(
            (updatedPrograms) => {
                queryClient.setQueryData<NowAndNextData>(NOW_AND_NEXT_KEY, (old) =>
                    old ? { ...old, currentPrograms: updatedPrograms } : old
                );
            },
            () => refetchRef.current()
        );
        scheduleManagerRef.current.start();

        return () => scheduleManagerRef.current?.stop();
    }, []);

    useEffect(() => {
        if (data) {
            scheduleManagerRef.current?.setData(data);
        }
    }, [data]);

    const items = useMemo(() => {
        const programs = data?.currentPrograms ?? [];
        const favoriteChannelIds = new Set(channels?.filter((channel) => channel.favorite).map((channel) => channel.id));

        if (!favoriteChannelIds.size) return programs;

        return [
            ...programs.filter((program) => favoriteChannelIds.has(program.channelId)),
            ...programs.filter((program) => !favoriteChannelIds.has(program.channelId)),
        ];
    }, [data?.currentPrograms, channels]);

    return {
        items,
        isLoading,
        error: error instanceof Error ? error.message : null,
        refetch,
    };
};
